import { ReactElement, useCallback, useMemo } from 'react'
import { AxiosError } from 'axios'
import type { ProjectConnectionPrioritizationDTO } from '@api/fastAPI.schemas'
import {
  getGetOrganisationConnectionPrioritizationQueryKey,
  useGetOrganisationConnectionPrioritization,
  useSetOrganisationConnectionPrioritization,
} from '@api/organisation-connection-prioritization/organisation-connection-prioritization'
import { useQueryClient } from '@tanstack/react-query'
import { Form } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import { buildErrorMessage } from 'src/constants/errors'
import OrganisationConnectionPrioritizationLists from './components/OrganisationConnectionPrioritizationLists'
import { schema } from './schema'

interface Props {
  connections: Connection[]
}

const FORM_ID = 'organisation-connection-prioritization-form'

const OrganisationConnectionPrioritizationForm = ({ connections }: Props): ReactElement | null => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  const { data, isLoading } = useGetOrganisationConnectionPrioritization<
    ProjectConnectionPrioritizationDTO,
    AxiosError
  >({
    query: {
      onError: error => {
        enqueueSnackbar(
          buildErrorMessage(error, 'Fehler beim Laden der Zugverbindungspriorisierung'),
          { variant: 'error' },
        )
      },
    },
  })

  const defaultValues = useMemo(() => data, [data])

  const { mutate: savePrioritization } = useSetOrganisationConnectionPrioritization<
    AxiosError,
    unknown
  >({
    mutation: {
      meta: {
        title: 'Priorisierung speichern',
        description: 'Speichert die Reihenfolge der Zugverbinder pro Szenario',
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries(getGetOrganisationConnectionPrioritizationQueryKey())
        enqueueSnackbar('Zugverbindungspriorisierung erfolgreich gespeichert', {
          variant: 'success',
        })
      },
      onError: error => {
        enqueueSnackbar(
          buildErrorMessage(error, 'Fehler beim Speichern der Zugverbindungspriorisierung'),
          { variant: 'error' },
        )
      },
    },
  })

  const onSave = useCallback(
    (values: ProjectConnectionPrioritizationDTO) => {
      savePrioritization({ data: values })
    },
    [savePrioritization],
  )

  if (isLoading || !defaultValues) return null

  return (
    <Form
      id={FORM_ID}
      onSubmit={() => undefined}
      validationSchema={schema}
      defaultValues={defaultValues}
    >
      <OrganisationConnectionPrioritizationLists connections={connections} onSave={onSave} />
    </Form>
  )
}

export default OrganisationConnectionPrioritizationForm
